// The item + totals section of the 32-column Bluetooth ESC/POS customer
// receipt, built from the backend's receipt envelope
// (`GET /retail/public/{slug}/orders/{code}/receipt`, i.e.
// `backend/payments/receipt.py::build_receipt`). It mirrors that module's
// own `to_escpos_bytes` layout so the browser-printed and server-printed
// receipts read the same.
//
// Product tax treatment (Oct 2026) — when the envelope carries
// `item_groups`, items are printed under TAXABLE ITEMS / NON-TAXABLE ITEMS,
// every taxable name ends in "(T)" (the NAME is shortened to fit, never the
// marker), the "(T) = Taxable item" legend follows, and the after-discount
// taxable / non-taxable subtotals are printed above Tax. An envelope without
// groups (an order with no per-line tax records) prints the flat list
// exactly as before. Every amount is the envelope's own; nothing is
// recomputed here.
//
// Returns [{ text, bold }] — the caller encodes and adds bold escapes.

const fmt = (c) => "$" + (Math.abs(Number(c) || 0) / 100).toFixed(2);
const row = (label, value) => label.padEnd(22) + value.padStart(10) + "\n";

function itemLines(it, { grouped }) {
  const out = [];
  const prefix = `${it.quantity}x `;
  let name;
  if (grouped && it.taxable) {
    const room = 22 - prefix.length - " (T)".length;
    name = `${(it.name || "").slice(0, Math.max(1, room))} (T)`;
  } else if (grouped) {
    name = (it.name || "").slice(0, 22 - prefix.length);
  } else {
    name = (it.name || "").slice(0, 24);
  }
  out.push({ text: (prefix + name).padEnd(22) + fmt(it.line_total_cents).padStart(10) + "\n" });
  // Customer-selected spice (Sep 2026, restored) — plain text on 32-column
  // thermal paper, mirroring `payments/receipt.py`.
  if (it.spice_level_label) out.push({ text: `   [${String(it.spice_level_label).toUpperCase()}]\n` });
  // Item-level Takeout marker (Sep 2026).
  if (it.item_takeout) out.push({ text: "   [TAKEOUT]\n" });
  if (it.note) out.push({ text: `   - ${String(it.note).slice(0, 28)}\n` });
  return out;
}

export function receiptBodyLines(receipt) {
  const lines = [];
  const groups = receipt.item_groups;
  if (groups && groups.length) {
    for (const g of groups) {
      if (!g.items || !g.items.length) continue;
      lines.push({ text: `${g.label}\n`, bold: true });
      for (const it of g.items) lines.push(...itemLines(it, { grouped: true }));
    }
    if (receipt.tax_legend) lines.push({ text: `${receipt.tax_legend}\n` });
  } else {
    for (const it of receipt.items || []) lines.push(...itemLines(it, { grouped: false }));
  }
  lines.push({ text: "-".repeat(32) + "\n" });

  const t = receipt.totals || {};
  lines.push({ text: row("Subtotal", fmt(t.subtotal_cents)) });
  // Each extra line prints only when non-zero, so an order without
  // discounts, delivery or tip prints exactly as before.
  if (t.discount_cents) {
    const couponLabel = t.coupon_code ? `Coupon ${t.coupon_code}`.slice(0, 21) : "Discount";
    lines.push({ text: row(couponLabel, "-" + fmt(t.discount_cents)) });
  }
  if (t.order_discount_cents) lines.push({ text: row("Order Discount", "-" + fmt(t.order_discount_cents)) });
  if (t.discount_cents && t.order_discount_cents) {
    lines.push({ text: row("Total discounts", "-" + fmt(t.total_discount_cents)) });
  }
  if (t.taxable_subtotal_cents !== undefined && t.taxable_subtotal_cents !== null) {
    lines.push({ text: row("Taxable subtotal", fmt(t.taxable_subtotal_cents)) });
    lines.push({ text: row("Non-taxable subtotal", fmt(t.non_taxable_subtotal_cents)) });
  }
  lines.push({ text: row("Tax", fmt(t.tax_cents)) });
  if (t.delivery_cents) lines.push({ text: row("Delivery", fmt(t.delivery_cents)) });
  if (t.tip_cents) lines.push({ text: row("Tip", fmt(t.tip_cents)) });
  lines.push({ text: row("TOTAL", fmt(t.total_cents)), bold: true });
  return lines;
}
